import React, { useCallback, useState } from 'react';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { Icons } from '../icons';

export type QueryComboboxOption = {
  value: string | number;
  label: string;
};

type BaseProps<TOption extends QueryComboboxOption> = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  options: TOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  emptyText?: string;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  renderOption?: (option: TOption) => React.ReactNode;
};

type MultipleProps<TOption extends QueryComboboxOption> = BaseProps<TOption> & {
  multiple: true;
  value?: TOption[];
  plural: string;
  onChange?: (value: TOption[]) => void;
  renderValue?: (value: TOption[]) => React.ReactNode;
};

type SingleProps<TOption extends QueryComboboxOption> = BaseProps<TOption> & {
  multiple?: false;
  value?: TOption | null;
  plural?: string;
  onChange?: (value?: TOption) => void;
  renderValue?: (value?: TOption | null) => React.ReactNode;
};

export type QueryComboboxProps<TOption extends QueryComboboxOption> =
  | MultipleProps<TOption>
  | SingleProps<TOption>;

export type QueryComboboxImplProps<TOption extends QueryComboboxOption> =
  | Omit<
      Extract<QueryComboboxProps<TOption>, { multiple: true }>,
      'options' | 'plural'
    >
  | Omit<
      Extract<QueryComboboxProps<TOption>, { multiple?: false }>,
      'options' | 'plural'
    >;

const defaultRenderOption = <TOption extends QueryComboboxOption>(
  option: TOption,
) => <span className="truncate">{option.label}</span>;

const QueryCombobox = <TOption extends QueryComboboxOption>({
  open,
  onOpenChange,
  options,
  placeholder = 'Selecione...',
  searchPlaceholder = 'Pesquisar...',
  searchValue,
  onSearchValueChange,
  emptyText = 'Nenhum resultado encontrado',
  isLoading,
  disabled,
  className,
  renderOption = defaultRenderOption,
  ...props
}: QueryComboboxProps<TOption>) => {
  const [internalOpen, setInternalOpen] = useState(open || false);
  const isOpen = open ?? internalOpen;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setInternalOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );

  const getValueContent = () => {
    if (props.renderValue) {
      return props.multiple
        ? props.renderValue(props.value ?? [])
        : props.renderValue(props.value);
    }

    if (props.multiple) {
      if (props.value?.length === 1) {
        return renderOption(props.value[0]);
      }

      return `${props.value?.length ?? 0} ${props.plural}`;
    }

    return props.value ? (
      renderOption(props.value)
    ) : (
      <span className="truncate text-muted-foreground">{placeholder}</span>
    );
  };

  const isSelected = (option: TOption) =>
    props.multiple
      ? !!props.value?.find((valueOption) => valueOption.value === option.value)
      : props.value?.value === option.value;

  const handleSelect = (option: TOption) => {
    if (props.multiple) {
      const selected = isSelected(option);
      props.onChange?.(
        selected
          ? (props.value?.filter(
              (valueOption) => valueOption.value !== option.value,
            ) ?? [])
          : [...(props.value ?? []), option],
      );

      return;
    }

    const selected = isSelected(option);
    props.onChange?.(selected ? undefined : option);
    handleOpenChange(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={isOpen}
          role="combobox"
          variant="outline"
          className={cn(
            'w-full justify-between border-input bg-background px-3 font-normal',
            'outline-none outline-offset-0 hover:bg-background focus-visible:outline-[3px]',
            className,
          )}
          disabled={disabled}
        >
          <div className="flex min-w-0 flex-1 items-center truncate text-left">
            {getValueContent()}
          </div>
          {isLoading ? (
            <Icons.spinner
              aria-hidden="true"
              className="ml-2 h-4 w-4 shrink-0 animate-spin"
            />
          ) : (
            <ChevronsUpDownIcon
              aria-hidden="true"
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-full min-w-[var(--radix-popper-anchor-width)] border-input p-0"
      >
        <Command filter={() => 1}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={onSearchValueChange}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const selected = isSelected(option);

                return (
                  <CommandItem
                    key={option.value}
                    value={option.value.toString()}
                    onSelect={() => handleSelect(option)}
                  >
                    {renderOption(option)}
                    {selected && <CheckIcon className="ml-auto h-4 w-4" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default QueryCombobox;
